import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  IconButton,
  Icon,
  Button,
  Tooltip,
  Spinner,
} from '@chakra-ui/react';
import {
  FiList,
  FiChevronRight,
  FiChevronLeft,
  FiDatabase,
  FiRefreshCw,
  FiWifiOff,
  FiAlertCircle,
  FiMap,
  FiLogOut,
  FiInfo,
  FiSlash,
  FiActivity,
  FiLogIn,
  FiExternalLink,
} from 'react-icons/fi';
import { getAccessLogs } from 'services/qrService';
import { getCustomersWithOptions } from 'services/woo';
import { normalizeCourseName, codeToDisplayLabel } from 'utils/courseUtils';
import AccessLogDetailModal from './AccessLogDetailModal';

// pedagogical/pickup son los 2 motivos vigentes; el resto queda para mostrar
// bien las salidas anticipadas ya registradas antes de este cambio.
const EXIT_REASON_LABELS = {
  pedagogical:   'Salida pedagógica',
  pickup:        'Retiro',
  sick:          'Enfermedad',
  medical:       'Cita médica',
  parent_pickup: 'Retiro apoderado',
  emergency:     'Emergencia',
  authorized:    'Act. autorizada',
  discipline:    'Disciplina',
  other:         'Otro',
};

// Etiqueta corta de tipo — mismo criterio que ACCESS_TYPE_CONFIG en
// AccessLogs/index.js, para que el panel simplificado hable el mismo
// lenguaje que la tabla completa.
const ACCESS_TYPE_LABEL = { entry: 'Entrada', exit: 'Salida', info: 'Info' };

// Fondo de la columna "tipo" — un degradado suave (mismo estilo que
// WARM_SHELL_BG del shell) en vez de un color plano, con los mismos tonos
// pastel que ya usa el resto de la app para cada tipo (mint=entrada,
// butter=salida, sky=consulta — ver MODE_BADGE en ScannerShell.js).
const ACCESS_TYPE_BG = {
  entry: { grad: 'linear-gradient(160deg, #E7F7EE 0%, #C7ECD7 100%)', ink: '#1E7A4E' },
  exit:  { grad: 'linear-gradient(160deg, #FDF1DD 0%, #F6DBAB 100%)', ink: '#B5631A' },
  info:  { grad: 'linear-gradient(160deg, #EAF2FD 0%, #CEE0FA 100%)', ink: '#2A66B4' },
};

// Mismo criterio de iconografía que usa AccessLogs/index.js (la tabla
// completa) para que el panel simplificado se lea igual — pero acá se arma
// a partir de un registro CRUDO de /qr/logs (access_type/early_exit/
// exit_reason/status_at_scan), no del shape local que usaba el historial de
// sesión anterior.
const historyIcon = (log) => {
  if (log.access_type === 'exit' && log.early_exit && log.exit_reason === 'pedagogical') return { icon: FiMap,        color: '#6D28D9' };
  if (log.access_type === 'exit' && log.early_exit)                                      return { icon: FiAlertCircle,color: '#DC4D20' };
  if (log.access_type === 'exit')                                                        return { icon: FiLogOut,     color: '#D97706' };
  if (log.access_type === 'info')                                                        return { icon: FiInfo,       color: '#2A66B4' };
  if (log.status_at_scan === 'suspended')                                                return { icon: FiSlash,      color: '#DC2626' };
  if (log.status_at_scan === 'medical_leave')                                            return { icon: FiActivity,   color: '#D97706' };
  return                                                                                         { icon: FiLogIn,      color: '#3DB871' };
};

// Misma ruta que abre "Ver perfil" (icono = nueva pestaña) en otras partes
// del CRM — así el operador no pierde la sesión de escaneo en curso.
const openFullHistory = () => {
  window.open(`${window.location.origin}/#/admin/accesos-qr`, '_blank');
};

const PANEL_W_COLLAPSED = '56px';

// Panel lateral derecho FIJO (siempre montado, a diferencia del Drawer que
// reemplaza) con el historial de accesos REAL del colegio (no solo lo
// escaneado en esta pestaña/sesión — trae los últimos registros ya
// persistidos vía /qr/logs, SIN filtrar por lector, la misma fuente y el
// mismo criterio por defecto que la pestaña "Historial de Accesos" de
// Accesos QR, acá en versión simplificada) y el estado del cache offline.
// Minimizable a un riel angosto de íconos para devolverle espacio a la
// cámara/lector cuando no hace falta consultarlo.
const HistoryPanel = ({
  collapsed,
  onToggleCollapse,
  refreshToken,
  cacheSize,
  cacheLoading,
  lastSyncDate,
  refreshCache,
  navbarOffset, // alto real del AdminNavbar fijo (ver NAVBAR_OFFSET en ScannerShell.js)
}) => {
  const [logs, setLogs]               = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError]     = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  // Nombre/curso reales por RUT — /qr/logs no siempre trae el nombre del
  // alumno (log.nombre viene vacío la mayoría de las veces), así que se
  // enriquece igual que AccessLogs/index.js: una consulta aparte a
  // /datofy/v1/users por los RUTs visibles. Sin esto el panel mostraba puro
  // RUT en vez del nombre.
  const [studentInfoByRut, setStudentInfoByRut] = useState({});
  const studentCacheRef = useRef({});

  const enrichStudents = useCallback(async (ruts) => {
    const toFetch = ruts.filter(r => r && !studentCacheRef.current[r]);
    if (!toFetch.length) return;
    const BATCH = 5;
    for (let i = 0; i < toFetch.length; i += BATCH) {
      const chunk = toFetch.slice(i, i + BATCH);
      const results = await Promise.all(chunk.map(rut =>
        getCustomersWithOptions({ search: rut, role: 'all', per_page: 5 })
          .then(res => (res.data ?? []).find(c => c.username === rut) ?? null)
          .catch(() => null)
      ));
      const update = {};
      chunk.forEach((rut, idx) => { update[rut] = results[idx] ?? {}; });
      Object.assign(studentCacheRef.current, update);
      setStudentInfoByRut(prev => ({ ...prev, ...update }));
    }
  }, []);

  // OJO — bug encontrado y corregido: esto ANTES filtraba por
  // `scanner_id: scannerId` (el lector activo). Cada lector tiene un `mode`
  // FIJO (access_log/exit_log/info_only/event_ticket — ver modeToAccessType
  // en ScannerShell.js), así que TODOS los registros de un mismo lector
  // quedan grabados con el MISMO access_type para siempre — filtrar por
  // scanner_id hacía que la columna "tipo" de cada fila mostrara siempre lo
  // mismo (lo que sea que el lector actual sea) en vez del dato real de
  // cada registro, y que cambiar de lector "arriba" hiciera que TODA la
  // lista pareciera cambiar de tipo de golpe — se leía como data falsa. La
  // pestaña completa "Historial de Accesos" (AccessLogs/index.js) NO filtra
  // por lector por defecto (scanner_id: '' = todos) — acá se hace lo mismo:
  // sin filtro de lector, solo los últimos accesos reales de todo el
  // colegio, con su tipo real cada uno. Se re-consulta cada vez que termina
  // un escaneo nuevo en esta pantalla (refreshToken, ver
  // ScannerShell::history.length) — así el panel refleja de inmediato lo que
  // el operador acaba de registrar, sin esperar a que alguien lo abra.
  useEffect(() => {
    let cancelled = false;
    setLogsLoading(true);
    getAccessLogs({ per_page: 20 })
      .then(res => {
        if (cancelled) return;
        const data = res.data ?? [];
        setLogs(data);
        setLogsError(false);
        enrichStudents([...new Set(data.map(l => l.rut))]);
      })
      .catch(() => { if (!cancelled) setLogsError(true); })
      .finally(() => { if (!cancelled) setLogsLoading(false); });
    return () => { cancelled = true; };
  }, [refreshToken, enrichStudents]);

  if (collapsed) {
    return (
      <VStack
        w={PANEL_W_COLLAPSED} h="100%" flexShrink={0} spacing="10px" pt={navbarOffset} pb={3}
        bg="white" borderLeft="1px solid" borderColor="cream.line" zIndex={5}
      >
        <Tooltip label="Mostrar historial" placement="left">
          <IconButton
            icon={<Icon as={FiChevronLeft} boxSize="20px" />} aria-label="Mostrar historial" onClick={onToggleCollapse}
            w="40px" h="40px" minW="40px" borderRadius="10px"
            bg="coral.tint" color="coral.600" border="1.5px solid" borderColor="coral.soft"
            _hover={{ bg: 'coral.soft', borderColor: 'coral.500' }}
          />
        </Tooltip>
        <Box position="relative">
          <Icon as={FiList} boxSize={5} color="ink.700" />
          {logs.length > 0 && (
            <Box position="absolute" top="-6px" right="-8px" bg="coral.500" color="#fff"
              borderRadius="full" fontSize="9px" fontWeight="800" minW="15px" h="15px" textAlign="center"
              lineHeight="15px" px="3px">
              {logs.length}
            </Box>
          )}
        </Box>
        <Tooltip label={cacheSize === 0 && !cacheLoading ? 'Cache vacío' : `${cacheSize} en cache`} placement="left">
          <Box>
            <Icon as={FiDatabase} boxSize={4} color={cacheSize === 0 && !cacheLoading ? 'butter.ink' : 'ink.400'} />
          </Box>
        </Tooltip>
      </VStack>
    );
  }

  return (
    <VStack
      w="33.333%" minW="240px" maxW="440px"
      h="100%" flexShrink={0} spacing={0} align="stretch"
      bg="white" borderLeft="1px solid" borderColor="cream.line" zIndex={5} overflow="hidden"
    >
      {/* BUG encontrado y corregido: a diferencia de la columna principal
          (que despeja el AdminNavbar fijo con pt={NAVBAR_OFFSET} en su barra
          superior), este panel arrancaba en y=0 — el encabezado ("Historial
          y cache" + el botón de minimizar) quedaba tapado por la navbar
          fija (position="fixed", h="60px", bg blanco) en TODOS los tamaños
          de pantalla por igual. Por eso "no se veían los botones": estaban
          ahí, pero literalmente debajo de la barra de arriba. */}
      <HStack px={3} pt={navbarOffset} pb={3} borderBottom="1px solid" borderColor="cream.line" flexShrink={0}>
        <Text fontSize="15.5px" fontWeight="800" color="ink.900" flex={1}>Historial y cache</Text>
        <Tooltip label="Minimizar historial" placement="bottom">
          <IconButton
            icon={<Icon as={FiChevronRight} boxSize="18px" />} aria-label="Minimizar historial" onClick={onToggleCollapse}
            w="36px" h="36px" minW="36px" borderRadius="9px"
            bg="cream.bg2" color="ink.700" border="1.5px solid" borderColor="cream.line2"
            _hover={{ bg: 'coral.soft', color: 'coral.600', borderColor: 'coral.300' }}
          />
        </Tooltip>
      </HStack>

      <VStack spacing="10px" align="stretch" px={3} py={3} flexShrink={0}>
        <HStack justify="space-between" flexWrap="wrap" gap={1}>
          <HStack spacing={1} color={cacheSize === 0 && !cacheLoading ? 'butter.ink' : 'ink.400'}>
            <Icon as={FiDatabase} boxSize={3.5} />
            <Text fontSize="13.5px" fontWeight="600">
              {cacheSize === 0 && !cacheLoading
                ? 'Cache vacío'
                : `${cacheSize} alumno${cacheSize !== 1 ? 's' : ''} en cache${lastSyncDate ? ` · ${lastSyncDate}` : ''}`}
            </Text>
          </HStack>
          <Button size="xs" variant="ghost" leftIcon={<FiRefreshCw />}
            isLoading={cacheLoading} onClick={() => refreshCache(true)} color="ink.400">
            Actualizar
          </Button>
        </HStack>
      </VStack>

      <Box borderTop="1px solid" borderColor="cream.line" mx={3} />

      <HStack px={3} pt={3} pb={1} flexShrink={0}>
        <Text fontSize="13.5px" fontWeight="700" color="ink.900" flex={1}>Últimos accesos</Text>
        {logsLoading && <Spinner size="xs" color="coral.500" />}
      </HStack>

      <VStack spacing="6px" align="stretch" px={3} pb={2} overflowY="auto" flex={1} sx={{ WebkitOverflowScrolling: 'touch' }}>
        {logsError ? (
          <Text fontSize="13.5px" color="danger.500" py={4} textAlign="center">
            No se pudo cargar el historial.
          </Text>
        ) : !logsLoading && logs.length === 0 ? (
          <Text fontSize="13.5px" color="ink.400" py={4} textAlign="center">
            Aún no hay accesos registrados.
          </Text>
        ) : (
          logs.map((log) => {
            const { icon, color } = historyIcon(log);
            const typeStyle = ACCESS_TYPE_BG[log.access_type] ?? ACCESS_TYPE_BG.entry;
            const info = studentInfoByRut[log.rut];
            const fullName = [info?.first_name, info?.last_name].filter(Boolean).join(' ').trim()
              || log.nombre || log.rut || '—';
            const rawGroup = info?.meta_data?.find(m => m.key === 'group_name')?.value;
            const courseLabel = rawGroup ? codeToDisplayLabel(normalizeCourseName(rawGroup)) : null;
            return (
              <HStack
                key={log.id} as="button" type="button" spacing="8px" bg="cream.bg" borderRadius="10px"
                px="10px" py="9px" minH="52px" align="flex-start" textAlign="left" w="100%" overflow="hidden"
                _hover={{ bg: 'coral.tint' }} transition="background .12s"
                onClick={() => setSelectedLog(log)}
                title="Ver detalle de este acceso"
              >
                <Icon as={icon} color={color} boxSize={4} mt="2px" flexShrink={0} />
                <VStack spacing={0} align="flex-start" flex={1} minW={0}>
                  <Text fontSize="13.5px" fontWeight="600" color="ink.900" noOfLines={1}>
                    {fullName}
                  </Text>
                  {log.early_exit && log.exit_reason && (
                    <Text fontSize="12px" color="#DC4D20" noOfLines={1} fontWeight="600">
                      {EXIT_REASON_LABELS[log.exit_reason] ?? log.exit_reason}
                    </Text>
                  )}
                  <HStack spacing="4px" flexWrap="wrap">
                    {log.rut && (
                      <Text fontSize="12px" color="ink.400" fontFamily="mono" noOfLines={1}>{log.rut}</Text>
                    )}
                    {courseLabel && (
                      <Text fontSize="12px" color="ink.400" noOfLines={1}>· {courseLabel}</Text>
                    )}
                    {log.location && (
                      <Text fontSize="12px" color="ink.400" noOfLines={1}>· {log.location}</Text>
                    )}
                    {log.is_offline && <Icon as={FiWifiOff} color="butter.ink" boxSize="10px" />}
                  </HStack>
                </VStack>
                <VStack spacing="2px" align="center" flexShrink={0} bg={typeStyle.grad}
                  borderRadius="8px" px="8px" py="5px">
                  <Text fontSize="10px" fontWeight="800" color={typeStyle.ink} textTransform="uppercase"
                    letterSpacing=".02em" whiteSpace="nowrap">
                    {ACCESS_TYPE_LABEL[log.access_type] ?? log.access_type}
                  </Text>
                  <Text fontSize="11px" color={typeStyle.ink} opacity={0.75} whiteSpace="nowrap">
                    {log.scanned_at ? new Date(log.scanned_at).toLocaleTimeString('es-CL', { hour: '2-digit', minute: '2-digit' }) : '—'}
                  </Text>
                </VStack>
              </HStack>
            );
          })
        )}
      </VStack>

      <Box px={3} py={3} borderTop="1px solid" borderColor="cream.line" flexShrink={0}>
        <Button
          w="full" size="md" h="44px" fontSize="14px" borderRadius="10px" variant="ghost" color="coral.600"
          rightIcon={<FiExternalLink size={14} />}
          _hover={{ bg: 'coral.tint' }}
          onClick={openFullHistory}
        >
          Ver historial completo
        </Button>
      </Box>

      <AccessLogDetailModal
        isOpen={!!selectedLog}
        onClose={() => setSelectedLog(null)}
        log={selectedLog}
      />
    </VStack>
  );
};

export default HistoryPanel;
